import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import PendingOrderCard from '../PendingOrderCard';
import { useLanguage } from '../../../context/LanguageContext';
import { occupationService, diningOptionService } from '../../../services/api';
import dedupeByName from '../../../utils/dedupeByName';

const TableOrderModal = ({
    isOpen,
    onClose,
    selectedTableOrder,
    selectedTableOrderKey,
    printedOrders,
    currency,
    pendingOrders,
    pendingOrderIds,
    tenantStorage,
    setPrintedOrders,
    setSelectedProducts,
    setSelectedDiningOption,
    setTableNumber,
    setAddServiceCharge,
    setAddTax,
    setDiscountAmount,
    setNotes,
    setSelectedPaymentMethod,
    setEditingOrderKey,
    setCurrentOrderId,
    setOrderToChangeTable,
    setNewTableNumber,
    setNewDiningOption,
    setChangeTableModalOpen,
    setCurrentConfirmOrder,
    setConfirmModalOpen,
    handleDeleteOrder,
    // Hand-over for a PAID order (client request 2026-08-26). Supplied by the
    // legacy till; the native surface uses OrdersPanelNative instead.
    handleClearPaidOrder,
    printableCategories,
    setSuccess,
    setError,
}) => {
    const { t } = useLanguage();
    const [occupation, setOccupation] = useState(null);

    useEffect(() => {
        if (!isOpen || !selectedTableOrder) { setOccupation(null); return; }
        const fetchOcc = async () => {
            try {
                const allDO = dedupeByName(await diningOptionService.getAll());
                const doObj = allDO.find(d => d.name === selectedTableOrder.diningOption);
                if (!doObj || !selectedTableOrder.tableNumber) return;
                const occ = await occupationService.getByTable(doObj.id, selectedTableOrder.tableNumber);
                setOccupation(occ);
            } catch { setOccupation(null); }
        };
        fetchOcc();
    }, [isOpen, selectedTableOrder]);

    const handleMarkServed = async () => {
        if (!occupation) return;
        try {
            await occupationService.markServed(occupation.id);
            setSuccess(t('table_marked_served') || 'Marked as served');
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed');
        }
    };

    const handleRequestBill = async () => {
        if (!occupation) return;
        try {
            await occupationService.requestBill(occupation.id);
            setSuccess(t('bill_requested') || 'Bill requested');
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed');
        }
    };

    const handleClearTable = async () => {
        if (!occupation) return;
        try {
            await occupationService.clearTable(occupation.id);
            setSuccess(t('table_cleared') || 'Table cleared');
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                selectedTableOrder ? (
                    <div>
                        <div>{selectedTableOrder.diningOption} {selectedTableOrder.tableNumber}</div>
                        <div className="text-sm text-gray-600 font-normal">
                            {t('staff')}: {selectedTableOrder.cashier || t('unknown')}
                        </div>
                    </div>
                ) : t('table_order')
            }
        >
            {selectedTableOrder && selectedTableOrderKey && (<>
                <PendingOrderCard
                    tableKey={selectedTableOrderKey}
                    order={selectedTableOrder}
                    isPrinted={printedOrders[selectedTableOrderKey] === true}
                    currency={currency}
                    printableCategories={printableCategories}
                    onClear={(tableKey) => {
                        if (handleClearPaidOrder) handleClearPaidOrder(tableKey);
                        onClose();
                    }}
                    onEdit={(tableKey) => {
                        // Use existing startEditPendingOrder logic
                        const order = pendingOrders[tableKey];
                        if (!order) return;

                        const productsWithUniqueIds = order.products.map(product => ({
                            ...product,
                            uniqueId: product.uniqueId || `${product.product_code}-${(product.add_ons || []).map(a => a.id).join('-')}-existing`
                        }));

                        setSelectedProducts(productsWithUniqueIds);
                        setSelectedDiningOption(order.diningOption);
                        setTableNumber(order.tableNumber);
                        setAddServiceCharge(order.addServiceCharge);
                        setAddTax(order.addTax || false);
                        setDiscountAmount(order.discount);
                        setNotes(order.notes);
                        setSelectedPaymentMethod(order.paymentMethod);
                        setEditingOrderKey(tableKey);
                        setCurrentOrderId(pendingOrderIds[tableKey]);

                        onClose();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onPrint={async (tableKey) => {
                        const orderId = pendingOrderIds[tableKey];
                        await tenantStorage.markOrderPrinted(tableKey, true, orderId);
                        setPrintedOrders(prev => ({ ...prev, [tableKey]: true }));
                        if (occupation) {
                            try { await occupationService.requestBill(occupation.id); } catch {}
                        }
                    }}
                    onCancelPrint={async (tableKey) => {
                        const orderId = pendingOrderIds[tableKey];
                        await tenantStorage.markOrderPrinted(tableKey, false, orderId);
                        setPrintedOrders(prev => {
                            const updated = { ...prev };
                            delete updated[tableKey];
                            return updated;
                        });
                    }}
                    onChangeTable={(tableKey) => {
                        // Close table modal and open change table modal
                        setOrderToChangeTable(tableKey);
                        setNewTableNumber('');
                        if (pendingOrders[tableKey]) {
                            setNewDiningOption(pendingOrders[tableKey].diningOption);
                        }
                        onClose();
                        setChangeTableModalOpen(true);
                    }}
                    onSubmit={(tableKey) => {
                        setCurrentConfirmOrder(tableKey);
                        onClose();
                        setConfirmModalOpen(true);
                    }}
                    onDelete={(tableKey) => {
                        handleDeleteOrder(tableKey);
                        onClose();
                    }}
                    occupation={occupation}
                    onMarkServed={() => handleMarkServed()}
                    setSuccess={setSuccess}
                    setError={setError}
                />
            </>)}
        </Modal>
    );
};

export default TableOrderModal;
